<template>
    <div class="row">
        <go-lotus-side-menu
            :current-component="goal ? goal.id : component"
            :top-level-property="'goal'"
            :side-menu-items="menuItems"
            :prop-to-set="'goal'"
            @set-parent-prop="propSetFromChild($event)"
        >
        </go-lotus-side-menu>

        <div class="col-md-10 main-content position-relative">
            <transition name="simple-fade" mode="out-in">
                <component
                    :is="component"
                    @set-parent-prop="propSetFromChild($event)"
                    @refresh-goals="getGoalsData($event)"
                    @goal-updated="updateGoal()"
                    :patient="patient"
                    :goals="goals"
                    :all-goals="allGoals"
                    :patients="patients"
                    :user-id="user.id"
                    :organization-id="organizationId"
                    :domains="domains"
                    :customizations="
                        customizations.sort((a, b) =>
                            a.formatted_name > b.formatted_name
                                ? 1
                                : a.formatted_name < b.formatted_name
                                ? -1
                                : 0
                        )
                    "
                    :goals-average-score="goalsAverageScore"
                    :goal="goal"
                ></component>
            </transition>
        </div>
    </div>
</template>

<script>
import GoLotusSideMenu from "./GoLotusSideMenu";
import Overview from "./goals/Overview";
import Detail from "./goals/Detail";
import DetailProbe from "./goals/DetailProbe";
import AllGoalsOverview from "./goals/AllGoalsOverview";
import SoapNotes from "./patient-progress/SoapNotes";
import ProgramNotes from "./patient-progress/ProgramNotes";
import { sideMenuItems } from "../plugins/sideMenuItems";
import { helpers } from "../plugins/helpers";
import { permissions } from "../plugins/permissions";

export default {
    name: "Goals",
    props: [
        "patient",
        "user",
        "organizationId",
        "patients",
        "allGoals",
        "goalDomainFilter",
        "domains",
        "customizations",
    ],
    components: {
        GoLotusSideMenu,
        Overview,
        Detail,
        DetailProbe,
        AllGoalsOverview,
        ProgramNotes,
        SoapNotes,
    },
    mixins: [sideMenuItems, permissions],
    data() {
        return {
            component: this.patient.id ? "overview" : "all-goals-overview",
            goalsAverageScore: [],
            goals: [],
            goal: null,
            requestedGoal: null,
        };
    },
    mounted() {
        this.getGoalsData();
        const goal = helpers.getRequestParam("goal");
        if (goal) {
            this.requestedGoal = goal;
        }
    },
    watch: {
        goal() {
            if (this.goal) {
                switch (this.goal.tracker_type) {
                    case "cold_probe":
                        this.component = "detail-probe";
                        break;
                    default:
                        this.component = "detail";
                }
            } else {
                this.component = "overview";
            }
        },
        component() {
            if (!["detail", "detail-probe"].includes(this.component)) {
                this.goal = null;
            }
        },
    },
    computed: {
        menuItems() {
            if (this.canViewGoalDetails) {
                return [
                    {
                        title: `${
                            this.patient && this.patient.name ? this.patient.name : ""
                        } Goals Overview`,
                        class: "patient-name py-3",
                        component: "overview",
                    },
                    { title: "", class: "separator" },
                    { title: "SOAP Notes", class: "", component: "soap-notes" },
                    {
                        title: "Program Notes",
                        class: "",
                        component: "program-notes",
                        shouldDisplay: () => this.$root.permissions.includes("client.notes.view"),
                    },
                    { title: "", class: "separator" },
                    ...this.goals.filter(goal => goal.domain.name === "Behavior"),
                    ...this.goals.filter(goal => goal.domain.name !== "Behavior"),
                ];
            } else {
                return [
                    {
                        title: `${
                            this.patient && this.patient.name ? this.patient.name : ""
                        } Goals Overview`,
                        class: "patient-name py-3",
                        component: "overview",
                    },
                    { title: "", class: "separator" },
                    { title: "SOAP Notes", class: "", component: "soap-notes" },
                    { title: "Program Notes", class: "", component: "program-notes" },
                    { title: "", class: "separator" },
                ];
            }
        },
    },
    methods: {
        getTrackerCustomizations() {
            axios
                .get(
                    `/api/users/${this.user.id}/tracker_customizations?organization=${
                        this.organizationId
                    }`
                )
                .then(r => {
                    this.customizations = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getGoalsData(filters) {
            this.goalsAverageScore = [];
            if (this.patient) {
                let params = {
                    params: {
                        filter_groups: [
                            {
                                filters: [],
                            },
                        ],
                    },
                };
                if (filters) {
                    filters.forEach(function(filter) {
                        params.params.filter_groups[0].filters.push(filter);
                    });
                } else {
                    params.params.filter_groups[0].filters.push({
                        key: "is_archived",
                        value: 0,
                        operator: "eq",
                    });
                }

                axios.get(`/api/patients/${this.patient.id}/goals/`, params).then(response => {
                    this.goals = response.data.data;
                    if (this.goal) {
                        this.goal = this.goals.find(g => g.id === this.goal.id);
                    }
                    if (this.requestedGoal) {
                        this.goal = this.goals.find(g => g.id === parseInt(this.requestedGoal));
                    }
                    this.buildGoalsAverageScores(response.data.meta.stats.progress_averages);
                });
            }
        },
        buildGoalsAverageScores(domainAverages) {
            this.goalsAverageScore = [];
            for (let abbreviation in domainAverages) {
                // find the domain that matches the abbreviation
                let domain = this.domains.find(function(d) {
                    return d.abbreviation == abbreviation;
                });

                if (!domain) {
                    break;
                }

                this.goalsAverageScore.push({
                    title: domain.name + " average",
                    color: domain.color,
                    progress: domainAverages[abbreviation],
                });
            }
        },

        updateGoal() {
            axios
                .get(`/api/patients/${this.patient.id}/goals/${this.goal.id}`)
                .then(r => {
                    this.goal = r.data.data;
                })
                .catch(e => {
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped lang="scss"></style>
